import React, {useEffect} from 'react';
import { useTranslation } from 'react-i18next';
import {ApplicationInterface, experienceLevels, KeyValueInterface} from "../interfaces";
import CustomButton from "../../../components/CustomButton";
import iconPencil from "url:shared-assets/icons/white/pencil.svg"
import {scrollTop} from "../../../helpers/scrolling";

interface Props {
    application: ApplicationInterface;
    save: Function
    changeStep: Function
    experiencesByArea: Array<KeyValueInterface>
    experiencesByIndustry: Array<KeyValueInterface>
    leadershipLevels: Array<KeyValueInterface>
    professionalAreas: Array<KeyValueInterface>
}

const experienceLength = ["No experience", "1-4 years", "5-9 years", "10+ years"]


const StepTen: React.FC<Props> = (props: Props) => {
    const translation = useTranslation();
    const { t } = translation;

    useEffect(() => {
        scrollTop()
    }, [])

    const showLanguages = (languages: Array<string>) => {
        let langString = ""
        for (let i = 0; i < languages.length; i++) {
            if (i > 0) langString += ", "
            langString += t(languages[i])
        }
        return langString
    }

    const showExperienceLevel = (value: number) => {
        for (const e of experienceLevels) {
            if (e.value === value) {
                return e.name
            }
        }
        return "Unknown"
    }

    const keyVal = (arr: Array<KeyValueInterface>, key: string) => {
        for (const e of arr) {
            if (e.key === key) return e.value
        }
        return key
    }

    const getFileName = (file: string) => {
        if (!file) {
            return ""
        }
        const fParts = file.split('/')
        return fParts[fParts.length - 1]
    }

    return (
      <>
          <div className="mt-6 text-xl font-medium">Summary</div>

          <div className="mt-6 flex items-center">
              <div className="text-lg font-medium">Personal details</div>
              <img alt="edit" src={iconPencil} className="ml-2 cursor-pointer" onClick={() => props.changeStep(0)}/>
          </div>

          <div className="mt-2 flex justify-between text-neutral-400">
              <p>First name</p>
              <p>{props.application.firstName}</p>
          </div>

          <div className="mt-1 flex justify-between text-neutral-400">
              <p>Surname</p>
              <p>{props.application.lastName}</p>
          </div>

          <div className="mt-1 flex justify-between text-neutral-400">
              <p>Phone number</p>
              <p>{props.application.phone}</p>
          </div>

          <div className="mt-1 flex justify-between text-neutral-400">
              <p>Company name</p>
              <p>{props.application.company}</p>
          </div>

          <div className="mt-1 flex justify-between text-neutral-400">
              <p>Organisational number</p>
              <p>{props.application.organisationNumber}</p>
          </div>

          <div className="mt-1 flex justify-between text-neutral-400">
              <p>Company address</p>
              <p>{props.application.companyAddress}</p>
          </div>

          <hr className="border-neutral-700 mt-6"/>

          <div className="mt-6 flex items-center">
              <div className="text-lg font-medium">Certification</div>
              <img alt="edit" src={iconPencil} className="ml-2 cursor-pointer" onClick={() => props.changeStep(1)}/>
          </div>

          <div className="mt-1 text-neutral-400">
              {getFileName(props.application.certificateFile)} has been uploaded
          </div>

          <div className="mt-1 flex justify-between text-neutral-400">
              <p>Year of certification</p>
              <p>{props.application.yearOfCertification}</p>
          </div>

          <div className="mt-1 flex justify-between text-neutral-400">
              <p>Total coaching hours</p>
              <p>{props.application.coachingHours}</p>
          </div>

          <hr className="border-neutral-700 mt-6"/>

          <div className="mt-6 flex items-center">
              <div className="text-lg font-medium">Resumé</div>
              <img alt="edit" src={iconPencil} className="ml-2 cursor-pointer" onClick={() => props.changeStep(2)}/>
          </div>

          <div className="mt-1 text-neutral-400">
              <p>{props.application.linkedinUrl}</p>
          </div>

          <hr className="border-neutral-700 mt-6"/>

          <div className="mt-6 flex items-center" onClick={() => props.changeStep(3)}>
              <div className="text-lg font-medium">Languages</div>
              <img alt="edit" src={iconPencil} className="ml-2 cursor-pointer" onClick={() => props.changeStep(3)}/>
          </div>

          <div className="mt-1 flex justify-between text-neutral-400">
              <p>Native</p>
              <p>{showLanguages(props.application.languagesNative)}</p>
          </div>
          <div className="mt-1 flex justify-between text-neutral-400">
              <p>Professional</p>
              <p>{showLanguages(props.application.languagesProfessional)}</p>
          </div>
          {
            props.application.additionalLanguages.length > 0 &&
              <div className="mt-1 flex justify-between text-neutral-400">
                  <p>Additional</p>
                  <p>{props.application.additionalLanguages}</p>
              </div>
          }

          <hr className="border-neutral-700 mt-6"/>

          <div className="mt-6 flex items-center">
              <div className="text-lg font-medium">Experience in coaching areas</div>
              <img alt="edit" src={iconPencil} className="ml-2 cursor-pointer" onClick={() => props.changeStep(4)}/>
          </div>

          {
              props.application.experienceByArea.filter(area => area.value > 0)
                .map(area => (
                  <div className="mt-1 flex justify-between text-neutral-400" key={"exp_by_area_" + area.name}>
                      <p>{keyVal(props.experiencesByArea, area.name)}</p>
                      <p>{showExperienceLevel(area.value)}</p>
                  </div>
                ))
          }

          {
            props.application.additionalAreas.length > 0 &&
              <div className="mt-1 flex justify-between text-neutral-400">
                  <p>Additional</p>
                  <p>{props.application.additionalAreas}</p>
              </div>
          }

          <hr className="border-neutral-700 mt-6"/>

          <div className="mt-6 flex items-center">
              <div className="text-lg font-medium">Coaching experience by industry</div>
              <img alt="edit" src={iconPencil} className="ml-2 cursor-pointer" onClick={() => props.changeStep(5)}/>
          </div>

          {
              props.application.experienceByIndustry.map(e => (
                <div className="mt-1 text-neutral-400" key={"exp_by_industry_" + e}>
                    {keyVal(props.experiencesByIndustry, e)}
                </div>
              ))
          }

          {
            props.application.additionalIndustriesClient.length > 0 &&
              <div className="mt-1 flex justify-between text-neutral-400">
                  <p>Additional</p>
                  <p>{props.application.additionalIndustriesClient}</p>
              </div>
          }

          <hr className="border-neutral-700 mt-6"/>

          <div className="mt-6 flex items-center">
              <div className="text-lg font-medium">Coaching experience by role</div>
              <img alt="edit" src={iconPencil} className="ml-2 cursor-pointer" onClick={() => props.changeStep(6)}/>
          </div>

          {
              props.application.experienceByRole.filter(e => e.value > 0).map(e => (
                <div className="mt-1 flex justify-between text-neutral-400" key={"exp_by_role_" + e.name}>
                    <p>{keyVal(props.leadershipLevels, e.name)}</p>
                    <p>{showExperienceLevel(e.value)}</p>
                </div>
              ))
          }

          <hr className="border-neutral-700 mt-6"/>

          <div className="mt-6 flex items-center">
              <div className="text-lg font-medium">Your professional experience</div>
              <img alt="edit" src={iconPencil} className="ml-2 cursor-pointer" onClick={() => props.changeStep(7)}/>
          </div>

          {
              props.application.experienceProfessional.filter(e => e.value > 0).map(e => (
                <div className="mt-1 flex justify-between text-neutral-400" key={"exp_prof_" + e.name}>
                    <p>{keyVal(props.professionalAreas, e.name)}</p>
                    <p>{experienceLength[e.value]}</p>
                </div>
              ))
          }

          <hr className="border-neutral-700 mt-6"/>

          <div className="mt-6 flex items-center">
              <div className="text-lg font-medium">Your industry experience</div>
              <img alt="edit" src={iconPencil} className="ml-2 cursor-pointer" onClick={() => props.changeStep(8)}/>
          </div>

          {
              props.application.workedInIndustry.map(e => (
                <div className="mt-1 text-neutral-400" key={"worked_industry_" + e}>
                    {keyVal(props.experiencesByIndustry, e)}
                </div>
              ))
          }

          {
            props.application.additionalIndustriesCoach.length > 0 &&
              <div className="mt-1 text-neutral-400">
                  {props.application.additionalIndustriesCoach}
              </div>
          }

          <CustomButton className="mt-6" onClick={() => props.save({})}>
              Submit
          </CustomButton>
      </>
    )
}

export default StepTen